import { useAnalytics } from "@codat/analytics";
import {
  IndicatorLabel,
  Tooltip,
  TooltipContent,
  Typography,
} from "@codat/orchard-ui";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";

import { ConnectionStatus, SourceType } from "../Companies";
import {
  getConnectionIcon,
  getConnectionInfoText,
  getConnectionLabelColor,
  getConnectionTooltipText,
} from "../utils/connectionStatus";
import styles from "./ConnectionButton.module.scss";

interface ConnectionButtonProps {
  platformName: string;
  status: ConnectionStatus;
  sourceType: SourceType;
  type: SourceType;
  onPlatformClick: () => void;
  connectionId?: string;
  companyId: string;
}

const HIDE_TOOLTIP_DELAY_MS = 250;

const ConnectionButton = ({
  platformName,
  status,
  onPlatformClick,
  sourceType,
  connectionId,
  companyId,
}: ConnectionButtonProps) => {
  const { sendEvent } = useAnalytics();
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<number | undefined>(undefined);

  const sendAnalyticsEventForConnectionClick = () => {
    sendEvent("Connection Button - Click", {
      connectionId,
      sourceType,
      companyId,
    });
  };

  const sendAnalyticsEventForTooltip = () => {
    sendEvent("Not Linked Tooltip - Open", {
      connectionId,
      sourceType,
      companyId,
    });
  };

  const sendAnalyticsEventForTooltipGetLink = () => {
    sendEvent("Not Linked Tooltip - Link URL Button - Click", {
      connectionId,
      sourceType,
      companyId,
    });
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const isNotAuthed =
    status === "Deauthorized" ||
    status === "Unlinked" ||
    status === "PendingAuth";

  const tooltipText = getConnectionTooltipText(status);

  return (
    <>
      <div
        className={styles.visuallyHidden}
        id={`connection-info-text-${connectionId}`}
      >
        {getConnectionInfoText(status)}
      </div>
      <Tooltip
        className={styles.connectionStatusTooltip}
        id={`connection-unlinked-tooltip-${connectionId}`}
        isOpen={showTooltip && isNotAuthed && !!tooltipText}
        placement={["top", "bottom"]}
        content={
          <TooltipContent
            title={
              <Typography
                className={styles.connectionStatusTooltipTitle}
                variant="heading_xl"
                as="div"
              >
                {tooltipText?.header}
              </Typography>
            }
          >
            <Typography variant="body_sm" as="p">
              {tooltipText?.body}
            </Typography>
            <button
              data-testid="link-url-link"
              className={styles.connectionStatusTooltipButton}
              onClick={(e) => {
                e.stopPropagation();
                sendAnalyticsEventForTooltipGetLink();
                onPlatformClick();
              }}
              onFocus={() => clearTimeout(timeoutRef.current)}
              onBlur={() => setShowTooltip(false)}
            >
              Get Link URL
            </button>
          </TooltipContent>
        }
      >
        <Link
          data-testid="connection-button"
          onMouseEnter={() => {
            if (isNotAuthed) {
              sendAnalyticsEventForTooltip();
              setShowTooltip(true);
            }
          }}
          onFocus={() => {
            if (isNotAuthed) {
              sendAnalyticsEventForTooltip();
              setShowTooltip(true);
            }
          }}
          onMouseLeave={() => {
            timeoutRef.current = window.setTimeout(
              () => setShowTooltip(false),
              HIDE_TOOLTIP_DELAY_MS
            );
          }}
          aria-describedby={
            isNotAuthed
              ? `connection-unlinked-tooltip-${connectionId}`
              : `connection-info-text-${connectionId}`
          }
          key={connectionId}
          className={styles.connectionButton}
          onClick={(e) => {
            e.stopPropagation();
            const isTouch =
              typeof document !== "undefined" &&
              "ontouchstart" in document.documentElement;
            if (isTouch) {
              sendAnalyticsEventForTooltip();
              setShowTooltip(true);
              return;
            }
            onPlatformClick();
            sendAnalyticsEventForConnectionClick();
          }}
          to={formatUrl(paths.companies.CONNECTIONS, {
            companyId,
          })}
        >
          <IndicatorLabel
            label={platformName}
            color={getConnectionLabelColor(status)}
            leftIcon={getConnectionIcon(status)}
          />
        </Link>
      </Tooltip>
    </>
  );
};

export default ConnectionButton;
